import { useEffect, useState } from 'react'
import { api } from '../services'
import { UserUnitSettings } from '../common/constants'
import { Spinner } from '../components'
import { useAccountContext } from '../contexts'

export function UserPreferences() {
  const { setUserUnitSettings: updateUserUnitSettings } = useAccountContext()

  const [userUnitSettingsOriginal, setUserUnitSettingsOriginal] = useState<UserUnitSettings>({})
  const [userUnitSettings, setUserUnitSettings] = useState<UserUnitSettings>({})
  const [selections, setSelections] = useState<Record<string, any>>({})
  const [dictionary, setDictionary] = useState<Record<string, string>>({})
  const [hasChanged, setHasChanged] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    api
      .getUserUnits()
      .then((result) => {
        setUserUnitSettingsOriginal(result.data.userUnitSettings)
        setUserUnitSettings(result.data.userUnitSettings)
        setSelections(result.data.selections)
        setDictionary(result.data.dictionary)
      })
      .catch((error) => {
        console.error('api.getUserUnits()', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    setHasChanged(Object.keys(difference()).length > 0)
  }, [userUnitSettings, userUnitSettingsOriginal]) // eslint-disable-line react-hooks/exhaustive-deps

  function updateUnits(unitCategoryId: string, unitId: number) {
    setUserUnitSettings({
      ...userUnitSettings,
      [unitCategoryId]: {
        ...userUnitSettings[unitCategoryId],
        unitId,
      },
    })
  }

  function updatePrecision(unitCategoryId: string, precision: number) {
    setUserUnitSettings({
      ...userUnitSettings,
      [unitCategoryId]: {
        ...userUnitSettings[unitCategoryId],
        precision,
      },
    })
  }

  function reset() {
    setUserUnitSettings(userUnitSettingsOriginal)
  }

  function save() {
    if (hasChanged) {
      // save user preferences to the backend
      setIsUpdating(true)

      api
        .updateUserUnitSettings(difference())
        .then((result) => {
          setUserUnitSettingsOriginal(result.data.current)
          setUserUnitSettings(result.data.current)
          updateUserUnitSettings(result.data.current)
        })
        .catch((error) => {
          console.error('api.updateUserUnitsSettings()', { error })
        })
        .finally(() => {
          setIsUpdating(false)
        })
    }
  }

  function difference() {
    const differences: Record<string, any> = {}

    for (const unitCategoryId in userUnitSettings) {
      if (userUnitSettings.hasOwnProperty(unitCategoryId)) {
        const originalUnit = userUnitSettingsOriginal[unitCategoryId]
        const currentUnit = userUnitSettings[unitCategoryId]

        if (
          originalUnit === undefined ||
          originalUnit.unitId !== currentUnit.unitId ||
          originalUnit.precision !== currentUnit.precision
        ) {
          if (originalUnit === undefined) {
            differences[unitCategoryId] = { ...currentUnit, unitId: 65 }
          } else {
            differences[unitCategoryId] = currentUnit
          }
        }
      }
    }

    return differences
  }

  return (
    <div className={tailwind.container}>
      <div className="flex flex-row w-100% justify-between">
        <h2>User Preferences</h2>
      </div>
      {isLoading || isUpdating ? (
        <div className={tailwind.spinner}>
          <Spinner />
        </div>
      ) : null}
      <table className={tailwind.table}>
        <thead>
          <tr className={tailwind.tableHeaderRow}>
            <th className={tailwind.tableCell}>Category</th>
            <th className={tailwind.tableCell}>Unit</th>
            <th className={tailwind.tableCell}>Precision</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(dictionary).map((unitCategoryId: string, index: number) => (
            <tr className={index % 2 ? tailwind.tableRow : tailwind.tableRowAlt} key={unitCategoryId}>
              <td className={tailwind.tableCell}>{dictionary[unitCategoryId]}</td>
              <td className={tailwind.tableCell}>
                <select
                  className={tailwind.selectBox}
                  value={userUnitSettings[unitCategoryId] ? userUnitSettings[unitCategoryId].unitId : undefined}
                  onChange={(e) => {
                    updateUnits(unitCategoryId, parseInt(e.target.value))
                  }}
                >
                  {selections[unitCategoryId] &&
                    selections[unitCategoryId].map((option: any, index: number) => {
                      return (
                        <option key={index} value={option.unitId}>
                          {option.shortName}
                        </option>
                      )
                    })}
                </select>
              </td>
              <td className={tailwind.tableCell}>
                <div className="flex flex-row">
                  <input
                    className={tailwind.inputText}
                    type="number"
                    id="precision"
                    placeholder={''}
                    value={userUnitSettings[unitCategoryId] ? userUnitSettings[unitCategoryId].precision : 0}
                    min={0}
                    onChange={(e) => {
                      updatePrecision(unitCategoryId, parseInt(e.target.value))
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end m-3">
        <button className={hasChanged ? tailwind.buttonEnabled : tailwind.buttonDisabled} onClick={reset}>
          Reset
        </button>
        <button className={hasChanged ? tailwind.buttonEnabled : tailwind.buttonDisabled} onClick={save}>
          Save
        </button>
      </div>
    </div>
  )
}

const tailwind = {
  container: 'mt-3 mx-10',

  buttonEnabled:
    'bg-blue-500 text-white font-bold py-1 px-4 rounded focus:outline-none focus:shadow-outline mb-3 mx-2 hover:bg-blue-700',

  buttonDisabled:
    'bg-gray-400 text-white font-bold py-1 px-4 rounded focus:outline-none focus:shadow-outline mb-3 mx-2',

  table: 'table-fixed w-full',

  tableHeaderRow: 'h-8 text-sm bg-gray-200',

  tableRow: 'text-sm bg-gray-100 border-b border-gray-2 ',

  tableRowAlt: 'text-sm border-b border-gray-2',

  tableCell: 'pl-3',

  selectBox: 'appearance-none border border-red-500 rounded w-full py-2 px-3 text-sm text-gray-700 leading-tight w-40',

  inputText:
    'appearance-none border border-red-500 rounded w-full py-2 px-3 text-sm text-gray-700 leading-tight focus:outline-none cursor-pointer w-20',

  spinner: 'absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50',
}
